<template>
    <DataTable
        :value="deposits"
        scrollable
        :scrollHeight="'80vh'"
        class="w-full mt-5"
        :loading="loading"
        :sortField="'totalDepositValue'"
        :sortOrder="-1"
        v-model:expandedRows="expandedRows"
        dataKey="clickId"
    >
        <Column expander style="width: 3rem" />

        <Column header="Click ID" field="clickId" sortable :headerStyle="headerStyles">
            <template #body="{ data }">
                <code class="text-xs">{{ data.clickId }}</code>
            </template>
        </Column>

        <Column header="Advertiser" field="advertiser" :headerStyle="headerStyles">
            <template #body="{ data }">
                {{ data.advertiser || 'N/A' }}
                <span v-if="data.market" :class="`fi fi-${data.market.toLowerCase()}`" class="ml-2"></span>
            </template>
        </Column>

        <Column header="Campaign ID" field="campaignId" :headerStyle="headerStyles" />

        <Column header="# Deposits" field="depositCount" sortable :headerStyle="headerStyles">
            <template #body="{ data }">
                <Tag :value="data.depositCount" severity="info" />
            </template>
        </Column>

        <Column header="Total Deposit Value" field="totalDepositValue" sortable :headerStyle="headerStyles">
            <template #body="{ data }">
                <span class="font-semibold text-green-400">{{ formatCurrency(data.totalDepositValue) }}</span>
            </template>
        </Column>

        <Column header="First Seen" field="firstSeen" sortable :headerStyle="headerStyles">
            <template #body="{ data }">{{ getFormattedDate(data.firstSeen) }}</template>
        </Column>

        <Column header="Last Seen" field="lastSeen" sortable :headerStyle="headerStyles">
            <template #body="{ data }">{{ getFormattedDate(data.lastSeen) }}</template>
        </Column>

        <template #expansion="{ data }">
            <div class="p-4 card-bg-dark">
                <h4 class="font-semibold mb-2">Individual deposits for click ID</h4>
                <DataTable :value="data.deposits" class="text-sm">
                    <Column header="Date" field="createdAt">
                        <template #body="{ data: d }">{{ getFormattedDate(d.createdAt) }}</template>
                    </Column>
                    <Column header="Event" field="eventType" />
                    <Column header="Deposit Value">
                        <template #body="{ data: d }">{{ formatCurrency(parseFloat(d.params?.depositValue) || 0) }}</template>
                    </Column>
                    <Column header="Status">
                        <template #body="{ data: d }">
                            <Tag :value="d.statusCode" :severity="d.statusCode === 200 ? 'success' : 'danger'" />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </template>

        <template #empty>
            <div class="text-center text-gray-400 py-8">
                No deposit postbacks found for this period.
            </div>
        </template>
    </DataTable>
</template>

<script setup>
import { shallowRef, onMounted, computed, ref, watch } from 'vue';
import PostbackService from '@/services/PostbackService';
import { headerStyles } from '@/global/headerStyles';
import { getFormattedDate } from '@/utils/dateHelper';
import { formatCurrency } from '@/utils/currencies';
import { useStore } from 'vuex';

const store = useStore();
const userId = computed(() => store.getters.userId);
const loading = shallowRef(true);
const rawPostbacks = shallowRef([]);
const expandedRows = ref([]);

const props = defineProps({
    dateRange: { type: Array, default: null }
});

const deposits = computed(() => {
    const withDeposits = rawPostbacks.value.filter(p =>
        p.params?.depositValue &&
        !isNaN(parseFloat(p.params.depositValue)) &&
        parseFloat(p.params.depositValue) > 0
    );

    const grouped = {};
    for (const p of withDeposits) {
        const clickId = p.params?.clickid || p.params?.clickId;
        if (!clickId || clickId.startsWith('{')) continue;

        if (!grouped[clickId]) {
            grouped[clickId] = {
                clickId,
                advertiser: p.advertiserId?.name || null,
                market: p.market,
                campaignId: p.campaignId,
                deposits: [],
                depositCount: 0,
                totalDepositValue: 0,
                firstSeen: p.createdAt,
                lastSeen: p.createdAt
            };
        }

        const entry = grouped[clickId];
        entry.deposits.push(p);
        entry.depositCount += 1;
        entry.totalDepositValue += parseFloat(p.params.depositValue) || 0;
        if (new Date(p.createdAt) < new Date(entry.firstSeen)) entry.firstSeen = p.createdAt;
        if (new Date(p.createdAt) > new Date(entry.lastSeen)) entry.lastSeen = p.createdAt;
    }

    return Object.values(grouped);
});

const fetchPostbacks = async () => {
    if (!props.dateRange || props.dateRange.length < 2 || !props.dateRange[1]) return;
    loading.value = true;
    try {
        const response = await PostbackService.getPostbackLog(userId.value, {
            from: props.dateRange[0],
            to: props.dateRange[1]
        });
        rawPostbacks.value = response.data;
    } catch (err) {
        console.error('Error fetching postback log:', err);
    } finally {
        loading.value = false;
    }
};

onMounted(fetchPostbacks);

watch(() => props.dateRange, fetchPostbacks, { deep: true });
</script>
